import { useEffect, useRef, useState } from "react";
import type { PlayerDto } from "@chess/shared";

export interface TeamOrderListProps {
  players: readonly PlayerDto[];
  meId: string;
  onReorder: (userIds: string[]) => void;
}

function move<T>(items: readonly T[], from: number, to: number): T[] {
  const next = [...items];
  const [lifted] = next.splice(from, 1);
  if (lifted !== undefined) next.splice(to, 0, lifted);
  return next;
}

/**
 * Drag-to-reorder list for a team's popcorn rotation.
 *
 * Dragging is the headline interaction, but every reorder is also reachable
 * from the two arrow buttons: HTML5 drag events are invisible to keyboards and
 * unreliable under touch, so on their own they'd make the mode unusable for
 * some players entirely.
 */
export function TeamOrderList({ players, meId, onReorder }: TeamOrderListProps) {
  const [order, setOrder] = useState<PlayerDto[]>(() => [...players]);
  const dragFrom = useRef<number | null>(null);

  // Follow the server unless this client is mid-drag, which would otherwise
  // yank the list out from under the pointer when a teammate's state arrives.
  const serverKey = players.map((player) => player.userId).join(",");
  useEffect(() => {
    if (dragFrom.current === null) setOrder([...players]);
    // players is a fresh array each broadcast; the id list is the real signal.
  }, [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function commit(next: PlayerDto[]) {
    setOrder(next);
    onReorder(next.map((player) => player.userId));
  }

  function nudge(index: number, delta: number) {
    const to = index + delta;
    if (to < 0 || to >= order.length) return;
    commit(move(order, index, to));
  }

  return (
    <ol className="order-list">
      {order.map((player, index) => (
        <li
          key={player.userId}
          className={`order-list__row${dragFrom.current === index ? " order-list__row--dragging" : ""}`}
          draggable
          onDragStart={(event) => {
            dragFrom.current = index;
            event.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(event) => {
            event.preventDefault();
            const from = dragFrom.current;
            if (from === null || from === index) return;
            // Reorder as the pointer passes, so the list previews the result.
            dragFrom.current = index;
            setOrder((current) => move(current, from, index));
          }}
          onDragEnd={() => {
            dragFrom.current = null;
            commit(order);
          }}
          onDrop={(event) => event.preventDefault()}
        >
          <span className="order-list__grip" aria-hidden>
            ⠿
          </span>
          <span className="order-list__seat">{index + 1}</span>
          <span className="order-list__name">
            {player.isBot ? <span className="tag tag--bot">BOT</span> : null}
            {player.username}
            {player.userId === meId ? <span className="tag tag--quiet">you</span> : null}
          </span>
          <span className="order-list__nudge">
            <button
              type="button"
              className="button--secondary button--compact"
              disabled={index === 0}
              aria-label={`Move ${player.username} earlier`}
              onClick={() => nudge(index, -1)}
            >
              ↑
            </button>
            <button
              type="button"
              className="button--secondary button--compact"
              disabled={index === order.length - 1}
              aria-label={`Move ${player.username} later`}
              onClick={() => nudge(index, 1)}
            >
              ↓
            </button>
          </span>
        </li>
      ))}
    </ol>
  );
}
